import React from "react";
import PropTypes from "prop-types";

import { gatherClasses, getPercentageValue } from "../../lib/utils";
import "./circularProgress.scss";

const strokeWidths = {
  xs: 2,
  sm: 2,
  md: 3,
  lg: 4
};

const CircularProgress = props => {
  const {
    max,
    value,
    size: propSize,
    dataTestId,
    label,
    labelPlacement,
    indeterminate,
    showPercProgress,
    valueLabel,
    strokeWidth: propStrokeWidth,
    strokeColor
  } = props;
  const percentage = Math.floor(getPercentageValue(max, value));

  const size = {
    xs: 28,
    sm: 36,
    md: 48,
    lg: 64
  }[propSize];

  const strokeWidth = propStrokeWidth || strokeWidths[propSize];

  const radius = size / 2 - 2;

  const circumference = 2 * Math.PI * radius;

  const dashArr1 = (circumference * percentage) / 100;

  const dashArr2 = circumference - dashArr1;

  return (
    <div
      data-testid={dataTestId}
      className={gatherClasses(
        props,
        "oui-progress-container",
        label
          ? `oui-labeled-progress oui-circular-labeled-progress oui-labeled-progress-${labelPlacement}`
          : null
      )}
    >
      <div
        className={gatherClasses(
          null,
          "oui-circular-progress",
          `oui-${propSize}`,
          indeterminate ? "oui-indeterminate-progress" : null
        )}
      >
        <svg
          className="oui-svg-container"
          height={size}
          width={size}
          viewBox={`0 0 ${size} ${size}`}
          strokeWidth={strokeWidth}
        >
          <circle
            className="oui-loader-svg oui-bg"
            cx={size / 2}
            cy={size / 2}
            r={radius}
          />
          {percentage && (
            <circle
              className="oui-loader-svg oui-fg"
              style={{ strokeDasharray: `${dashArr1} ${dashArr2}` }}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={strokeColor}
            />
          )}
          {!indeterminate && showPercProgress && (
            <text
              className="oui-perc-progress"
              x="50%"
              y={size === 36 ? "53%" : "50%"}
              dominantBaseline="middle"
              textAnchor="middle"
            >
              {valueLabel || `${percentage}%`}
            </text>
          )}
        </svg>
      </div>
      {label && (
        // eslint-disable-next-line jsx-a11y/label-has-associated-control
        <label
          className={gatherClasses(
            null,
            "oui-progress-label",
            `oui-progress-label-${labelPlacement}`
          )}
        >
          {label}
        </label>
      )}
    </div>
  );
};

CircularProgress.propTypes = {
  size: PropTypes.oneOf(["xs", "sm", "md", "lg"]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  indeterminate: PropTypes.bool,
  showPercProgress: PropTypes.bool,
  // eslint-disable-next-line react/no-unused-prop-types
  renderPercProgress: PropTypes.func,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  labelPlacement: PropTypes.oneOf(["right", "bottom"]),
  // eslint-disable-next-line react/no-unused-prop-types
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  valueLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  strokeWidth: PropTypes.number,
  strokeColor: PropTypes.string
};

CircularProgress.defaultProps = {
  size: "md",
  value: 0,
  max: 100,
  indeterminate: false,
  showPercProgress: true,
  label: null,
  labelPlacement: "bottom",
  dataTestId: "orion-progress-bar",
  strokeColor: "#2171b5"
};

CircularProgress.displayName = "OrionCircularProgress";

export default CircularProgress;
